/**
 * NewRuleDialog Component
 *
 * Dialog for manually creating a new learned rule
 * - Mandate-specific notice
 * - Pattern type selection
 * - Account mapping
 * - VAT configuration
 * - Confidence settings
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LearningRulesService } from '@/services/learningRulesService';
import { useToast } from '@/hooks/use-toast';
import type { PatternType, CreateRuleRequest } from '@/types/learningRules';
import { Loader2, Info } from 'lucide-react';

interface NewRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mandateId: string;
  mandateName: string;
  onSuccess?: () => void;
}

export const NewRuleDialog: React.FC<NewRuleDialogProps> = ({
  open,
  onOpenChange,
  mandateId,
  mandateName,
  onSuccess,
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  // Form state
  const [patternType, setPatternType] = useState<PatternType>('counterparty');
  const [patternKey, setPatternKey] = useState('');
  const [patternValue, setPatternValue] = useState('');
  const [debitAccount, setDebitAccount] = useState('');
  const [creditAccount, setCreditAccount] = useState('');
  const [vatCode, setVatCode] = useState('');
  const [vatAccount, setVatAccount] = useState('');
  const [descriptionTemplate, setDescriptionTemplate] = useState('');
  const [baseConfidence, setBaseConfidence] = useState(80);
  const [isShared, setIsShared] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!patternKey.trim()) {
      toast({
        title: 'Fehler',
        description: 'Pattern Key ist erforderlich.',
        variant: 'destructive',
      });
      return;
    }

    if (!debitAccount.trim() || !creditAccount.trim()) {
      toast({
        title: 'Fehler',
        description: 'Debit und Credit Konten sind erforderlich.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const ruleData: CreateRuleRequest = {
        mandate_id: mandateId,
        pattern_type: patternType,
        pattern_key: patternKey.trim(),
        pattern_value: patternValue.trim() || undefined,
        suggested_debit_account: debitAccount.trim(),
        suggested_credit_account: creditAccount.trim(),
        vat_code: vatCode.trim() || undefined,
        vat_account: vatAccount.trim() || undefined,
        description_template: descriptionTemplate.trim() || undefined,
        base_confidence: baseConfidence / 100,
        is_shared: isShared,
        scope: isShared ? 'organization' : 'user',
      };

      await LearningRulesService.createRule(ruleData);

      toast({
        title: 'Erfolg',
        description: `Regel für "${patternKey}" wurde erstellt.`,
      });

      // Reset form
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error creating rule:', error);
      toast({
        title: 'Fehler',
        description: 'Regel konnte nicht erstellt werden.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPatternType('counterparty');
    setPatternKey('');
    setPatternValue('');
    setDebitAccount('');
    setCreditAccount('');
    setVatCode('');
    setVatAccount('');
    setDescriptionTemplate('');
    setBaseConfidence(80);
    setIsShared(false);
  };

  const getPatternTypeDescription = (type: PatternType) => {
    switch (type) {
      case 'counterparty':
        return 'Regel basiert auf Gegenpartei (z.B. "COOP", "MIGROS")';
      case 'description_pattern':
        return 'Regel basiert auf Beschreibungsmuster (z.B. "Gehalt", "Miete")';
      case 'amount_based':
        return 'Regel basiert auf Betragsmuster (z.B. wiederkehrende Beträge)';
      case 'semantic':
        return 'Regel basiert auf semantischer Ähnlichkeit (AI-gestützt)';
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Neue Regel erstellen</DialogTitle>
          <DialogDescription>
            Erstelle eine manuelle Klassifikationsregel für{' '}
            <Badge className="ml-1">{mandateName}</Badge>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Mandate Notice */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Diese Regel gilt nur für <strong>{mandateName}</strong> und wird
              automatisch auf passende Transaktionen angewendet.
            </AlertDescription>
          </Alert>

          {/* Pattern Type */}
          <div className="space-y-2">
            <Label htmlFor="patternType">
              Pattern Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={patternType}
              onValueChange={(value) => setPatternType(value as PatternType)}
            >
              <SelectTrigger id="patternType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="counterparty">Counterparty</SelectItem>
                <SelectItem value="description_pattern">
                  Description Pattern
                </SelectItem>
                <SelectItem value="amount_based">Amount-Based</SelectItem>
                <SelectItem value="semantic">Semantic</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {getPatternTypeDescription(patternType)}
            </p>
          </div>

          {/* Pattern Key */}
          <div className="space-y-2">
            <Label htmlFor="patternKey">
              Pattern Key <span className="text-destructive">*</span>
            </Label>
            <Input
              id="patternKey"
              placeholder="z.B. COOP, MIGROS, Gehalt"
              value={patternKey}
              onChange={(e) => setPatternKey(e.target.value)}
              required
            />
            <p className="text-xs text-muted-foreground">
              Der Hauptbegriff, nach dem gesucht wird (z.B. Gegenpartei-Name)
            </p>
          </div>

          {/* Pattern Value (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="patternValue">Pattern Value (Optional)</Label>
            <Input
              id="patternValue"
              placeholder="Zusätzlicher Matching-Wert"
              value={patternValue}
              onChange={(e) => setPatternValue(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Zusätzliches Matching-Kriterium (nur bei speziellen Patterns)
            </p>
          </div>

          {/* Account Mapping */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="debitAccount">
                Debit Konto <span className="text-destructive">*</span>
              </Label>
              <Input
                id="debitAccount"
                placeholder="z.B. 6500"
                value={debitAccount}
                onChange={(e) => setDebitAccount(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="creditAccount">
                Credit Konto <span className="text-destructive">*</span>
              </Label>
              <Input
                id="creditAccount"
                placeholder="z.B. 1020"
                value={creditAccount}
                onChange={(e) => setCreditAccount(e.target.value)}
                required
              />
            </div>
          </div>

          {/* VAT Configuration */}
          <div className="space-y-4 pt-2 border-t">
            <h4 className="text-sm font-medium">VAT Konfiguration (Optional)</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="vatCode">MWST Code</Label>
                <Input
                  id="vatCode"
                  placeholder="z.B. V77, VR77"
                  value={vatCode}
                  onChange={(e) => setVatCode(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vatAccount">MWST Konto</Label>
                <Input
                  id="vatAccount"
                  placeholder="z.B. 2200"
                  value={vatAccount}
                  onChange={(e) => setVatAccount(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Description Template */}
          <div className="space-y-2">
            <Label htmlFor="descriptionTemplate">
              Beschreibungsvorlage (Optional)
            </Label>
            <Input
              id="descriptionTemplate"
              placeholder="z.B. Verpflegung COOP"
              value={descriptionTemplate}
              onChange={(e) => setDescriptionTemplate(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Vorlage für die Buchungsbeschreibung
            </p>
          </div>

          {/* Base Confidence */}
          <div className="space-y-3 pt-2 border-t">
            <div className="flex justify-between items-center">
              <Label>Start Confidence</Label>
              <Badge variant="outline">{baseConfidence}%</Badge>
            </div>
            <Slider
              value={[baseConfidence]}
              onValueChange={(values) => setBaseConfidence(values[0])}
              min={50}
              max={95}
              step={5}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Startvertrauen der Regel (Standard: 80%)
            </p>
          </div>

          {/* Share with Organization */}
          <div className="flex items-center space-x-2 pt-2 border-t">
            <Checkbox
              id="isShared"
              checked={isShared}
              onCheckedChange={(checked) => setIsShared(checked as boolean)}
            />
            <Label
              htmlFor="isShared"
              className="text-sm font-normal cursor-pointer"
            >
              Mit Organisation teilen
            </Label>
          </div>
          {isShared && (
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                Diese Regel wird für alle Benutzer in Ihrer Organisation
                verfügbar sein.
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird erstellt...
                </>
              ) : (
                'Regel erstellen'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NewRuleDialog;
